import { useEffect, useState } from "react";

import { getCustomizationBackgroundImageUrl } from "services/customizationService";
import { LS_CUSTOMIZATION_SET_EVENT_KEY } from "constants/constants";

import type { CustomizationBackground } from "types/customizationType";

// Already in the browser cache (e.g. from the previous page): known synchronously, so the frames show without a flash.
function isImageAlreadyLoaded(imageUrl: string | null): boolean {
    if (!imageUrl) {
        return false;
    }

    const image = new Image();
    image.src = imageUrl;

    return image.complete && 0 < image.naturalWidth;
}

// True only once the page's own image is actually displayed: a declared but broken URL (404, expired...) keeps
// the original look. Each page has its own image: an entity may customize the login page but not the vote page.
export const useHasBackgroundImage = (
    customizationBackground: CustomizationBackground,
): boolean => {
    const [backgroundImageUrl, setBackgroundImageUrl] = useState(() =>
        getCustomizationBackgroundImageUrl(customizationBackground),
    );
    // Only a successful load sets it: a broken or outdated image can never match the current URL.
    const [loadedImageUrl, setLoadedImageUrl] = useState(() =>
        isImageAlreadyLoaded(backgroundImageUrl) ? backgroundImageUrl : null,
    );

    // On a real subdomain the customization is fetched after the first render.
    useEffect(() => {
        function handleCustomizationSet() {
            setBackgroundImageUrl(
                getCustomizationBackgroundImageUrl(customizationBackground),
            );
        }

        handleCustomizationSet();
        window.addEventListener(
            LS_CUSTOMIZATION_SET_EVENT_KEY,
            handleCustomizationSet,
        );

        return () => {
            window.removeEventListener(
                LS_CUSTOMIZATION_SET_EVENT_KEY,
                handleCustomizationSet,
            );
        };
    }, [customizationBackground]);

    useEffect(() => {
        if (!backgroundImageUrl) {
            return;
        }

        const image = new Image();

        function handleImageLoad() {
            setLoadedImageUrl(backgroundImageUrl);
        }

        image.addEventListener("load", handleImageLoad);
        image.src = backgroundImageUrl;

        return () => {
            image.removeEventListener("load", handleImageLoad);
        };
    }, [backgroundImageUrl]);

    return null !== backgroundImageUrl && loadedImageUrl === backgroundImageUrl;
};
